import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useSessionGuard } from "../lib/useSessionGuard";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth or opening a magic sign-in link: let that page establish the session first.
    if (window.location.hash?.includes("session_id=") || window.location.pathname.startsWith("/magic")) {
      setUser(false);
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logoutWithReason = useCallback(async (reason) => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("impersonation_token");
    const emp = window.location.pathname.startsWith("/me") || window.location.pathname.startsWith("/employee");
    setUser(false);
    window.location.href = `${emp ? "/employee/login" : "/login"}?reason=${reason}`;
  }, []);
  useSessionGuard(user, logoutWithReason);

  useEffect(() => {
    // Server says the session is gone (idle / 1 AM cutoff / revoked): send the user back to sign in with the reason.
    const id = api.interceptors.response.use((r) => r, (err) => {
      const url = err?.config?.url || "";
      if (err?.response?.status === 401 && user && !url.includes("/auth/")) logoutWithReason("expired");
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, [user, logoutWithReason]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("impersonation_token");
    setUser(false);
    window.location.href = "/login";
  };

  const isImpersonating = !!(typeof window !== "undefined" && localStorage.getItem("impersonation_token"));

  const exitImpersonation = async () => {
    localStorage.removeItem("impersonation_token");
    window.location.href = "/companies";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, isImpersonating, exitImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
